/**
 * Calculation utilities for Payroll Reports
 */

import { calculateNightDifferentialHours } from './payrollUniqueTime';

/**
 * Calculate night differential hours for a specific date from unique job spans
 */
export const calculateNightDiffForDate = (date, spansByDate, payrollData, nightStart, nightEnd, calculateNightDifferentialFromSpans) => {
  // Use the spans data if available (accounts for overlapping jobs)
  if (spansByDate[date] && spansByDate[date].length > 0) {
    return calculateNightDifferentialFromSpans(
      spansByDate[date],
      nightStart,
      nightEnd
    ).toFixed(2);
  }
  
  // Fallback to individual job calculation if spans data not available
  const jobsForDate = payrollData.filter(job => job.job_date === date);
  let totalNightDiffHours = 0;
  
  jobsForDate.forEach(job => {
    if (job.start_time && job.end_time && !job.isManual && !job.isBreakdown && !job.isPaidLeave && !job.isDeduction) {
      const nightDiffHours = calculateNightDifferentialHours(
        job.job_date,
        job.start_time,
        job.end_time,
        nightStart,
        nightEnd
      );
      totalNightDiffHours += nightDiffHours;
    }
  });
  
  return totalNightDiffHours.toFixed(2);
};

/**
 * Calculate daily hours for a date
 */
export const calculateDailyHours = (jobsForDate) => {
  return jobsForDate
    .filter(job => !job.isDeduction)
    .reduce((sum, job) => sum + (job.workHours || 0), 0);
};

/**
 * Calculate total hours from payroll data
 */
export const calculateTotalHours = (payrollData) => {
  return payrollData
    .filter(job => !job.isDeduction)
    .reduce((sum, job) => sum + (job.workHours || 0), 0);
};

/**
 * Check if date has actual job orders
 */
export const hasActualJobOrders = (jobsForDate) => {
  return jobsForDate.some(job => !job.isPaidLeave && !job.isManual && !job.isBreakdown && !job.isDeduction);
};

/**
 * Check if date has job orders (excluding manual, breakdown, paid leave)
 */
export const hasJobOrders = (jobsForDate) => {
  return jobsForDate.some(job => !job.isManual && !job.isBreakdown && !job.isPaidLeave);
};

/**
 * Count jobs for display
 */
export const countJobsForDisplay = (jobsForDate, paidLeaveDates) => {
  return jobsForDate.filter(job => {
    // Include paid leave entries explicitly by checking multiple conditions
    if (job.isPaidLeave || job.id === 'PAID_LEAVE' || paidLeaveDates[job.job_date]) {
      return true;
    }
    // Include regular job orders (exclude manual entries, deductions, and breakdowns)
    return !job.isManual && !job.isDeduction && !job.isBreakdown;
  }).length;
};

/**
 * Group payroll data by date
 */
export const groupPayrollDataByDate = (payrollData, startDate, endDate) => {
  const allDatesInRange = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  
  while (currentDate <= end) {
    allDatesInRange.push(new Date(currentDate).toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Group jobs by date
  const groupedByDate = payrollData.reduce((acc, job) => {
    const date = job.job_date || 'No Date';
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(job);
    return acc;
  }, {});

  // Ensure all dates in range are represented in groupedByDate
  allDatesInRange.forEach(date => {
    if (!groupedByDate[date]) {
      groupedByDate[date] = [];
    }
  });

  return groupedByDate;
};

/**
 * Sort dates from grouped data
 */
export const sortDates = (groupedByDate) => {
  return Object.keys(groupedByDate).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    return new Date(a) - new Date(b);
  });
};

/**
 * Categorize dates by job orders status
 */
export const categorizeDates = (sortedDates, groupedByDate, paidLeaveDates) => {
  const datesWithJobOrdersOrPaidLeave = [];
  const datesWithoutJobOrdersOrPaidLeave = [];

  sortedDates.forEach(date => {
    const jobsForDate = groupedByDate[date];
    const hasJobOrdersFlag = hasJobOrders(jobsForDate);
    const hasPaidLeave = paidLeaveDates[date];

    if (hasJobOrdersFlag || hasPaidLeave) {
      datesWithJobOrdersOrPaidLeave.push(date);
    } else {
      datesWithoutJobOrdersOrPaidLeave.push(date);
    }
  });

  return { datesWithJobOrdersOrPaidLeave, datesWithoutJobOrdersOrPaidLeave };
};
